// utils
import { ampli } from '@/ampli';
import { useCallback } from 'react';

import { resetAllFeatureFlags, useEnabledFeatures } from '@/hooks/useFeature';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useWalletConnectionModal } from '@/hooks/useWalletConnectionModal';

import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { buttons } from '../../../styles/buttons';
import { AccountButtonDropdown } from '../AccountButtonDropdown';

interface AppConnectButtonProps {
  /** The button's children. */
  children: React.ReactNode;
  /** The callback to run when the button is clicked. */
  onClick: () => void;
  /** Additional classname */
  className?: string;
}

/*
 * A button to display when the user is disconnected.
 *
 * @param {AppConnectButtonProps} props - The component's props.
 */
export const AppConnectButton = ({
  children,
  onClick,
  className,
}: AppConnectButtonProps): React.ReactElement => (
  <button
    className={buttons({
      size: 'medium',
      color: 'black',
      shape: 'pill',
      class: ['text-nowrap', className],
    })}
    onClick={onClick}
  >
    {children}
  </button>
);

/**
 * Component that renders the connect button in the header, or the account
 * details & network select dropdowns if the user is connected.
 */
export const HeaderConnectButton = ({
  source = 'header',
}: {
  source?: 'header' | 'trade_history_page';
}): React.ReactElement => {
  const { solanaWallet, ethereumWallet } = useMatchaWallets();
  const { startConnectFlow } = useWalletConnectionModal();

  const handleConnectClick = (): void => {
    const ampliSource = source === 'header' ? 'primary_button' : 'trade_history_page';
    ampli.walletConnectClicked({ source: ampliSource, ui_location: source });
    startConnectFlow({ source: ampliSource });
  };

  // not connected
  const shouldShowConnectButton = !solanaWallet && !ethereumWallet;

  if (shouldShowConnectButton) {
    return <AppConnectButton onClick={handleConnectClick}>Connect Wallet</AppConnectButton>;
  }

  // connected account
  return <AccountButtonDropdown />;
};

/**
 * Component that renders the feature flag indicator in the header.
 */
export const FeatureFlagIndicator = () => {
  const featureStatus = useEnabledFeatures();

  const reset = useCallback(() => {
    resetAllFeatureFlags();
    window.location.replace('/');
  }, []);

  if (!featureStatus.length) return null;

  const featuresString = featureStatus.map((f) => `"${f}"`).join(', ');

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <div
          className={buttons({
            size: 'medium',
            color: 'white',
            shape: 'pill',
            class: ['hidden:block px-[13px]'],
          })}
          hidden={!featureStatus.length}
        >
          ⛳️
        </div>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>
        <div className="flex flex-col gap-2">
          <span>
            You have the {featuresString} feature flag{featureStatus.length > 1 && 's'} enabled.
          </span>
          <button
            className={buttons({
              size: 'small',
              color: 'lightRed',
              shape: 'pill',
              class: ['inline-block', 'h-7'],
            })}
            onClick={reset}
          >
            Reset {featureStatus.length > 1 && 'all'}
          </button>
        </div>
      </MatchaTooltipContent>
      <MatchaTooltipPopover sideOffset={8} />
    </MatchaTooltip>
  );
};
